import React, { useState, useEffect } from "react";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";
import PDF from "@/assets/images/ai-tools/chat-with-anything/pdf.svg";
import PPT from "@/assets/images/ai-tools/chat-with-anything/ppt.svg";
import CSV from "@/assets/images/ai-tools/chat-with-anything/excel.svg";
import TXT from "@/assets/images/ai-tools/chat-with-anything/txt.svg";
import DOC from "@/assets/images/ai-tools/chat-with-anything/docx.svg";
import IMAGE from "@/assets/images/ai-tools/chat-with-anything/image.svg";
import { ActionButtonProps } from "./ChatListing/Types/Types";

interface FileUsecaseTypes {
  icon: string;
  title: string;
  description: string;
  route: string;
  mimeTypes: string[];
  fileTypes: string;
}

export const getFileURL = (key: string) => {
  return `https://kroolo-qastage.s3.amazonaws.com/${key}`;
};

export const FileExtensionsWithRoutesAndIcons: FileUsecaseTypes[] = [
  {
    title: "Chat with PDF",
    description:
      "Extract key insights and summarize complex concepts from pdf files. Quickly analyze reports and papers.",
    route: "chat-pdf",
    icon: PDF,
    mimeTypes: ["application/pdf"],
    fileTypes: "pdf",
  },
  {
    title: "Chat with PPT",
    description:
      "Extract insights from ppt and ppt presentations. Summarize slide decks and identify key visual points.",
    route: "chat-ppt",
    icon: PPT,
    mimeTypes: [
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
    fileTypes: "ppt and pptx",
  },
  {
    title: "Chat with xls",
    description:
      "Process and analyze csv, xls, and xlsx spreadsheets. Extract insights and identify patterns from data.",
    route: "chat-xls",
    icon: CSV,
    mimeTypes: [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
    fileTypes: "csv, xls, and xlsx",
  },
  {
    title: "Chat with TXT",
    description:
      "Process and analyze txt files efficiently. Summarize content and extract main ideas from txt files.",
    route: "chat-txt",
    icon: TXT,
    mimeTypes: ["text/plain"],
    fileTypes: "txt",
  },
  {
    title: "Chat with DOC",
    description:
      "Analyze doc and docx files efficiently. Extract key information and summarize content from documents.",
    route: "chat-doc",
    icon: DOC,
    mimeTypes: [
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    fileTypes: "doc and docx",
  },
  {
    title: "Chat with Image",
    description:
      "Analyze and interpret jpeg, jpg and png images. Identify objects and extract insights from visual content.",
    route: "chat-image",
    icon: IMAGE,
    mimeTypes: ["image/jpeg", "image/png"],
    fileTypes: "jpeg, jpg, and png",
  },
];

export const allMimeTypes = [
  "application/pdf",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "text/markdown",
  "text/html",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.presentation",
  "application/x-mobipocket-ebook",
];

type ToastType = "success" | "error" | "info" | "warning";

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose?: () => void;
}

export const Toast: React.FC<ToastProps> = ({
  message,
  type = "error",
  duration = 3000,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const icons = {
    success: <CheckCircle className="h-5 w-5 text-green-400" />,
    error: <AlertCircle className="h-5 w-5 text-red-400" />,
    info: <Info className="h-5 w-5 text-blue-400" />,
    warning: <AlertTriangle className="h-5 w-5 text-yellow-400" />,
  };

  const backgrounds = {
    success: "border-green-500/20 bg-gray-900",
    error: "border-red-500/20 bg-gray-900",
    info: "border-blue-500/20 bg-gray-900",
    warning: "border-yellow-500/20 bg-gray-900",
  };

  if (!isVisible) return null;

  return (
    <div className="animate-fade-in fixed right-4 top-4 z-50">
      <div
        className={`flex items-center gap-3 rounded-lg border p-4 ${backgrounds[type]} min-w-[320px] bg-opacity-95 shadow-lg backdrop-blur-sm`}
      >
        {icons[type]}
        <p className="flex-1 text-sm text-gray-100">{message}</p>
        <button
          onClick={() => {
            setIsVisible(false);
            onClose?.();
          }}
          className="text-gray-400 transition-colors hover:text-gray-200"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export const ActionButton: React.FC<ActionButtonProps> = ({
  icon,
  onClick,
  title,
  type,
  className = "",
}) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1 rounded p-1 text-gray-500 transition-colors hover:text-[#202020] ${
      type === "down" ? "ml-auto" : ""
    } ${className}`}
    title={title}
  >
    {icon}
  </button>
);
